import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Cog, ChevronRight, Stethoscope, Heart, Activity, Shield, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function DepartmentOverview() {
  const structure = [
    {
      level: 'Executive Leadership',
      icon: Building2,
      description: 'Strategic direction and overall management of the hospital',
      units: ['Chief Medical Officer', 'Hospital Administrator', 'Director of Nursing', 'Finance Director']
    },
    {
      level: 'Clinical Departments',
      icon: Users,
      description: 'Direct patient care and medical services',
      units: ['General Medicine', 'Pediatrics', 'Maternity & Obstetrics', 'Surgery', 'Emergency Medicine', 'Pharmacy']
    },
    {
      level: 'Support Services',
      icon: Cog,
      description: 'Essential support for clinical operations',
      units: ['Laboratory Services', 'Radiology & Imaging', 'Medical Records', 'Patient Services', 'Maintenance']
    }
  ];

  const departmentFeatures = [
    {
      icon: Stethoscope,
      title: 'Coordinated Care',
      description: 'Our departments work together seamlessly to provide comprehensive patient care.'
    },
    {
      icon: Clock,
      title: 'Efficient Delivery',
      description: 'Streamlined processes ensure timely and effective healthcare service delivery.'
    },
    {
      icon: Shield,
      title: 'Quality Standards',
      description: 'Each department adheres to the highest medical and ethical standards.'
    },
    {
      icon: Heart,
      title: 'Patient-Centered',
      description: 'Every unit is designed with patient comfort and care as the priority.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-20 md:py-28" data-testid="dept-overview-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">About Us</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Department Overview
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Baobab Hospital operates through different units and service departments to ensure coordinated and efficient healthcare delivery. Our organizational structure supports clear lines of accountability and seamless patient care.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-spacing" data-testid="dept-overview-intro">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Structure</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
              How We Are Organized
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              At Baobab Hospital, our departmental structure is designed to facilitate efficient healthcare delivery while maintaining the highest standards of patient care. Each department plays a vital role in our mission to serve the Mapinga community and neighboring areas.
            </p>
          </div>

          {/* Department Features */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {departmentFeatures.map((feature, index) => (
              <Card key={index} className="card-service text-center" data-testid={`dept-feature-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-primary text-lg mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Organizational Structure */}
      <section className="section-spacing bg-warm" data-testid="dept-overview-structure">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Organizational Chart</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Hospital Departments
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our hospital is organized into functional departments that work together to deliver comprehensive healthcare services.
            </p>
          </div>

          <div className="space-y-12">
            {structure.map((level, index) => (
              <div key={index} data-testid={`structure-level-${index}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center shadow-lg">
                    <level.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-primary">{level.level}</h3>
                    <p className="text-slate-600">{level.description}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pl-20">
                  {level.units.map((unit, i) => (
                    <Card key={i} className="bg-white hover:bg-teal-light transition-colors group">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <ChevronRight className="w-5 h-5 text-primary" />
                        </div>
                        <span className="font-medium text-primary">{unit}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality & Governance */}
      <section className="section-spacing" data-testid="dept-overview-quality">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Quality Assurance</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                Governance & Quality Standards
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our organizational structure supports clear lines of accountability and continuous quality improvement. Each department follows established protocols and participates in regular performance reviews to ensure we maintain the highest standards of patient care.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Through regular departmental meetings, cross-functional collaboration, and continuous professional development, we ensure that every unit operates efficiently and effectively in service of our patients.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-warm p-4 rounded-xl text-center">
                  <p className="font-heading text-2xl font-bold text-primary">ISO</p>
                  <p className="text-slate-600 text-sm mt-1">Quality Standards</p>
                </div>
                <div className="bg-warm p-4 rounded-xl text-center">
                  <p className="font-heading text-2xl font-bold text-primary">24/7</p>
                  <p className="text-slate-600 text-sm mt-1">Supervision</p>
                </div>
                <div className="bg-warm p-4 rounded-xl text-center">
                  <p className="font-heading text-2xl font-bold text-primary">Monthly</p>
                  <p className="text-slate-600 text-sm mt-1">Quality Reviews</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1579165466741-7f35e4755660?w=600"
                alt="Healthcare quality"
                className="rounded-2xl shadow-float w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary" data-testid="dept-overview-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Explore Our Clinical Departments
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Learn more about the specialized medical services we offer across our various departments.
          </p>
          <Link to="/departments" data-testid="dept-overview-departments">
            <Button className="bg-accent hover:bg-coral-soft text-white rounded-full px-8 py-4 font-semibold shadow-lg">
              View All Departments
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
